import { useMemo } from 'react'
import { useLingui } from '@lingui/react/macro'

export const useHttpErrorMsg = (): Record<string, () => string> => {
	const { t } = useLingui()

	return useMemo(
		() => ({
			'payment.method.blocked': () => t`Votre compte est bloqué.`,
			'policy.account.business.ro_methods': () =>
				t`Impossible de modifier les moyens de paiements d'un compte entreprise`,
			'policy.availability.extra': () =>
				t`L'extra n'est pas disponible sur ce créneau/cette salle`,
			'policy.availability.space': () =>
				t`L'espace demandé n'est pas disponible`,
			'policy.cancel': () => t`Impossible d'annuler cet évènement`,
			'policy.change.cancelled': () =>
				t`Impossible de modifier une résa annulée`,
			'policy.change.readonly': () => t`La résa n'est pas modifiable`,
			'policy.change.refund_in_progress': () =>
				t`Un remboursement est en cours`,
			'policy.change.toolate': () =>
				t`Impossible de modifier une résa hors délai`,
			'policy.code.already_used': () => t`Ce code a déjà été utilisé`,
			'policy.code.invalid.day_of_week': () =>
				t`Impossible d'utiliser un code le week-end`,
			'policy.code.invalid.duration': () =>
				t`Impossible d'utiliser un code pour une résa de moins de 1h30`,
			'policy.code.invalid.first_booking': () =>
				t`Impossible d'utiliser un code pour la première résa`,
			'policy.code.invalid.gofather': () =>
				t`Vous ne pouvez pas utiliser votre propre code parrain`,
			'policy.code.invalid.hours': () =>
				t`Impossible d'utiliser un code avant 9h ou après 18h`,
			'policy.duration': () => t`Durée invalide`,
			'policy.event.in_past': () => t`Impossible de réserver dans le passé`,
			'policy.extra.time': () => t`Il est trop tard pour modifier cet extra`,
			'policy.organizer.account': () =>
				t`L'organisateur n'appartient pas au même compte que vous`,
			'policy.status.expired': () => t`La réservation a expiré`,
			'policy.visit.deny': () =>
				t`Impossible de réserver une visite sur ce créneau/cette salle`,
			'policy.visit.invalid': () => t`Visite invalide`,
			'policy.visit.quotas': () => t`Quota de visites épuisé`,
			'user.already_in_account': () =>
				t`L'utilisateur que vous tentez d'ajouter a déjà été invité.`,
			'user.conflict': () =>
				t`L'utilisateur que vous tentez d'ajouter a déjà un compte avec l'email.`,
			'booking.policy.availability.space': () =>
				t`Le créneau sélectionné n'est plus disponible.`,
			'booking.policy.visit.deny': () =>
				t`Impossible de réserver une visite sur ce créneau/cette salle`,
			'booking.policy.visit.invalid': () => t`Visite invalide`,
			'booking.policy.visit.quotas': () =>
				t`Vous avez atteint le nombre maximum de visites`,
			'extend.policy.status.expired': () =>
				t`Oups, vous avez dépassé le délai pour finaliser votre réservation. Aucune inquiétude, nous allons immédiatement vous rembourser.`,
			'auth/account-exists-with-different-credential': () =>
				t`Ce compte est rattaché à un autre fournisseur`,
			'auth/email-already-in-use': () => t`Cet e-mail est déjà utilisé`,
			'auth/expired-action-code': () => t`Le lien a expiré`,
			'auth/internal-error': () =>
				t`Le serveur d'authentification a rencontré une erreur inattendue`,
			'auth/invalid-action-code': () => t`Le lien est invalide`,
			'auth/invalid-email': () => t`L'e-mail fourni est invalide`,
			'auth/popup-closed-by-user': () => t`La popup de connexion a été fermée`,
			'auth/too-many-requests': () =>
				t`Veuillez réessayer dans quelques secondes`,
			'auth/user-disabled': () =>
				t`Le compte rattaché à ce lien a été désactivé`,
			'auth/user-mismatch': () => t`Identifiants incorrects`,
			'auth/user-not-found': () =>
				t`Aucun compte rattaché à cet e-mail n'a été trouvé`,
			'auth/weak-password': () =>
				t`Le mot de passe doit faire au moins 6 caractères`,
			'auth/wrong-password': () => t`Le mot de passe est incorrect`,
			'auth/claims-too-large': () =>
				t`Les informations personnalisées associées au compte sont trop volumineuses`,
			'auth/email-already-exists': () => t`Cet e-mail est déjà utilisé`,
			'auth/id-token-expired': () => t`Votre session a expiré`,
			'auth/id-token-revoked': () => t`Votre session a été révoquée`,
			'auth/insufficient-permission': () =>
				t`Vous n'avez pas les autorisations nécessaires pour effectuer cette action`,
			'auth/invalid-argument': () => t`Un paramètre fourni est invalide`,
			'auth/invalid-claims': () =>
				t`Les informations personnalisées associées au compte sont invalides`,
			'auth/invalid-continue-uri': () => t`L'URL de redirection est invalide`,
			'auth/invalid-creation-time': () => t`La date de création est invalide`,
			'auth/invalid-credential': () =>
				t`Les identifiants d'authentification ne permettent pas cette action`,
			'auth/invalid-disabled-field': () =>
				t`La valeur indiquant si le compte est désactivé est invalide`,
			'auth/invalid-display-name': () => t`Le nom affiché est invalide`,
			'auth/invalid-dynamic-link-domain': () =>
				t`Le domaine du lien dynamique n'est pas autorisé`,
			'auth/invalid-email-verified': () =>
				t`La valeur de vérification de l'e-mail est invalide`,
			'auth/invalid-hash-algorithm': () =>
				t`L'algorithme de hachage est invalide`,
			'auth/invalid-hash-block-size': () =>
				t`La taille de bloc de hachage est invalide`,
			'auth/invalid-hash-derived-key-length': () =>
				t`La longueur de clé dérivée du hachage est invalide`,
			'auth/invalid-hash-key': () => t`La clé de hachage est invalide`,
			'auth/invalid-hash-memory-cost': () =>
				t`Le coût mémoire du hachage est invalide`,
			'auth/invalid-hash-parallelization': () =>
				t`Le niveau de parallélisation du hachage est invalide`,
			'auth/invalid-hash-rounds': () =>
				t`Le nombre d'itérations de hachage est invalide`,
			'auth/invalid-hash-salt-separator': () =>
				t`Le séparateur de sel de hachage est invalide`,
			'auth/invalid-id-token': () => t`Le jeton d'identification est invalide`,
			'auth/invalid-last-sign-in-time': () =>
				t`La date de dernière connexion est invalide`,
			'auth/invalid-page-token': () => t`Le jeton de pagination est invalide`,
			'auth/invalid-password': () =>
				t`Le mot de passe doit faire au moins 6 caractères`,
			'auth/invalid-password-hash': () =>
				t`Le hachage du mot de passe est invalide`,
			'auth/invalid-password-salt': () =>
				t`Le sel du mot de passe est invalide`,
			'auth/invalid-phone-number': () => t`Le numéro de téléphone est invalide`,
			'auth/invalid-photo-url': () => t`L'URL de la photo est invalide`,
			'auth/invalid-provider-data': () =>
				t`Les données du fournisseur de connexion sont invalides`,
			'auth/invalid-provider-id': () =>
				t`L'identifiant du fournisseur de connexion est invalide`,
			'auth/invalid-oauth-responsetype': () =>
				t`Le type de réponse OAuth est invalide`,
			'auth/invalid-session-cookie-duration': () =>
				t`La durée du cookie de session est invalide`,
			'auth/invalid-uid': () => t`L'identifiant utilisateur est invalide`,
			'auth/invalid-user-import': () =>
				t`Les informations du compte à importer sont invalides`,
			'auth/maximum-user-count-exceeded': () =>
				t`Le nombre maximum de comptes à importer a été dépassé`,
			'auth/missing-android-pkg-name': () =>
				t`Le nom du package Android est requis`,
			'auth/missing-continue-uri': () =>
				t`Une URL de redirection valide est requise`,
			'auth/missing-hash-algorithm': () =>
				t`L'algorithme de hachage est requis`,
			'auth/missing-ios-bundle-id': () =>
				t`L'identifiant du bundle iOS est requis`,
			'auth/missing-oauth-client-secret': () =>
				t`Le secret client OAuth est requis`,
			'auth/missing-uid': () => t`L'identifiant utilisateur est requis`,
			'auth/operation-not-allowed': () =>
				t`Cette méthode de connexion n'est pas activée`,
			'auth/phone-number-already-exists': () =>
				t`Ce numéro de téléphone est déjà utilisé`,
			'auth/project-not-found': () => t`Le projet Firebase est introuvable`,
			'auth/reserved-claims': () =>
				t`Certaines informations personnalisées sont réservées`,
			'auth/session-cookie-expired': () => t`Votre session a expiré`,
			'auth/session-cookie-revoked': () => t`Votre session a été révoquée`,
			'auth/uid-already-exists': () =>
				t`Cet identifiant utilisateur est déjà utilisé`,
			'auth/unauthorized-continue-uri': () =>
				t`Le domaine de l'URL de redirection n'est pas autorisé`,
			else: () => t`Erreur du serveur distant`,
			mustBeAuthToBook: () =>
				t`Vous devez être connecté(e) pour réserver un Cocoon`,
			mustBeAuthToBookDesk: () =>
				t`Vous devez être connecté(e) pour réserver un Desk`,
			noScopesInUrl: () =>
				t`La page n'est pas accessible, veuillez vérifier le lien.`,
			'user.factuInfosIncomplete': () =>
				t`Informations de facturation incomplètes.`,
			'user.persoInfosIncomplete': () =>
				t`Informations personnelles incomplètes.`,
			default: () => t`Une erreur est survenue`,
		}),
		[t],
	)
}
